import { useEffect, useState } from "react";
import axios from "axios";
import { Link, Navigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { useLang, WHATSAPP_NUMBER } from "@/lib/i18n";
import { Button } from "@/components/ui/button";
import { Inbox, MessageCircle, Phone, CheckCircle2, Clock, ArrowLeft } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS = { all: "Tous", pending: "En attente", handled: "Traités" };

export default function ExpertRequests() {
  const { user, loading } = useAuth();
  const { t } = useLang();
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("pending");
  const [loadingList, setLoadingList] = useState(true);

  const fetchAll = async () => {
    setLoadingList(true);
    try {
      const status = filter === "all" ? "" : `?status=${filter}`;
      const { data } = await axios.get(`${API}/owner/expert-requests${status}`, { withCredentials: true });
      setItems(data.items || []);
    } catch (e) {
      toast.error("Erreur de chargement");
    } finally {
      setLoadingList(false);
    }
  };

  useEffect(() => {
    if (user && (user.role === "owner" || user.role === "trainer")) fetchAll();
  }, [user, filter]);

  if (loading) return null;
  if (!user || (user.role !== "owner" && user.role !== "trainer")) {
    return <Navigate to="/login" replace />;
  }

  const handle = async (id) => {
    try {
      await axios.post(`${API}/owner/expert-requests/${id}/handle`, {}, { withCredentials: true });
      toast.success("Marqué comme traité");
      fetchAll();
    } catch (e) {
      toast.error("Erreur");
    }
  };

  const pending = items.filter((r) => r.status === "pending").length;
  const dashboardLink = user.role === "owner" ? "/dashboard/owner" : "/dashboard/trainer";

  return (
    <div className="min-h-screen bg-[#FAFAF7]">
      <header className="border-b border-[#E2E4DF] bg-white">
        <div className="max-w-6xl mx-auto px-6 py-5 flex items-center justify-between">
          <Link to={dashboardLink} className="flex items-center gap-2 text-sm font-semibold text-[#1C3A27] hover:text-[#E05A3D]">
            <ArrowLeft size={16} /> Retour au tableau de bord
          </Link>
          <span className="font-display text-xl font-black text-[#1C3A27]">Mazra3ti</span>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 py-10 md:py-14">
        <div className="flex items-center gap-4 mb-2">
          <div className="w-12 h-12 rounded-2xl bg-[#E05A3D] grid place-items-center">
            <Inbox size={22} className="text-white" />
          </div>
          <div>
            <h1 data-testid="expert-requests-title" className="font-display text-3xl md:text-4xl font-black text-[#1C3A27]">
              File des questions experts
            </h1>
            <p className="text-sm text-[#5C6B5C] mt-1">
              {pending} en attente · {items.length} affiché(es)
            </p>
          </div>
        </div>

        {/* Filter tabs */}
        <div data-testid="expert-requests-tabs" className="mt-6 inline-flex rounded-2xl border border-[#E2E4DF] bg-white p-1">
          {Object.entries(STATUS).map(([k, label]) => (
            <button
              key={k}
              data-testid={`expert-tab-${k}`}
              onClick={() => setFilter(k)}
              className={`px-4 py-2 rounded-xl text-sm font-semibold transition-colors ${
                filter === k ? "bg-[#1C3A27] text-white" : "text-[#5C6B5C] hover:text-[#1C3A27]"
              }`}
            >
              {label}
            </button>
          ))}
        </div>

        {/* List */}
        <div className="mt-8 space-y-4">
          {loadingList ? (
            <p className="text-center py-12 text-[#5C6B5C]">Chargement…</p>
          ) : items.length === 0 ? (
            <div data-testid="expert-requests-empty" className="rounded-3xl border-2 border-dashed border-[#E2E4DF] p-12 text-center">
              <CheckCircle2 size={36} className="mx-auto text-emerald-500" />
              <p className="mt-3 text-[#5C6B5C]">Aucune question dans cette catégorie.</p>
            </div>
          ) : (
            items.map((r) => (
              <article
                key={r.id}
                data-testid={`expert-request-${r.id}`}
                className="rounded-2xl border border-[#E2E4DF] bg-white p-6"
              >
                <div className="flex flex-wrap items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <h3 className="font-display text-lg font-bold text-[#1C3A27]">{r.subject}</h3>
                      <span
                        className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider ${
                          r.type === "whatsapp"
                            ? "bg-emerald-100 text-emerald-800"
                            : "bg-blue-100 text-blue-800"
                        }`}
                      >
                        {r.type === "whatsapp" ? <MessageCircle size={10} /> : <Phone size={10} />}
                        {r.type === "whatsapp" ? "WhatsApp" : "Rappel"}
                      </span>
                      <span
                        className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider ${
                          r.status === "pending"
                            ? "bg-amber-100 text-amber-800"
                            : "bg-emerald-100 text-emerald-800"
                        }`}
                      >
                        {r.status === "pending" ? <Clock size={10} /> : <CheckCircle2 size={10} />}
                        {r.status === "pending" ? "En attente" : "Traité"}
                      </span>
                    </div>
                    <p className="text-sm text-[#5C6B5C] mt-1">
                      {r.user_name} ({r.user_email}) · {r.farm_type} · Plan {r.plan}
                    </p>
                    <p className="text-sm text-[#1C3A27] whitespace-pre-line mt-3 leading-relaxed">
                      {r.message}
                    </p>
                    <p className="text-xs text-[#A8AFA1] mt-3">
                      {new Date(r.created_at).toLocaleString()}
                      {r.handled_at && ` · Traité par ${r.handled_by} le ${new Date(r.handled_at).toLocaleString()}`}
                    </p>
                  </div>
                  <div className="flex flex-col gap-2">
                    {r.status === "pending" && (
                      <>
                        <a
                          href={`https://wa.me/?text=${encodeURIComponent(
                            `Bonjour ${r.user_name?.split(" ")[0] || ""},\n\nMerci pour votre question : "${r.subject}".\n\nVoici ma réponse :\n\n— Seifeddine, Mazra3ti`
                          )}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          data-testid={`expert-reply-wa-${r.id}`}
                          className="inline-flex items-center justify-center gap-2 h-10 px-4 rounded-xl bg-[#25D366] hover:bg-[#1FAD52] text-white text-sm font-semibold"
                        >
                          <MessageCircle size={14} />
                          Répondre sur WhatsApp
                        </a>
                        <Button
                          data-testid={`expert-mark-handled-${r.id}`}
                          variant="outline"
                          onClick={() => handle(r.id)}
                          className="h-10 rounded-xl"
                        >
                          <CheckCircle2 size={14} className="me-2" />
                          Marquer traité
                        </Button>
                      </>
                    )}
                  </div>
                </div>
              </article>
            ))
          )}
        </div>
      </main>
    </div>
  );
}
